/**
 * Flow Progress Component
 *
 * HUD-style bar showing current node, step progress, countdown timer,
 * and match context pills (game, queue, format, team, host, etc.).
 */

import type { FlowContext, FlowPlayer } from "@rivaled/shared";
import { getTeamNames } from "@rivaled/shared";
import {
  Crosshair,
  Gamepad2,
  KeyRound,
  Layers,
  Map as MapIcon,
  Shield,
  Swords,
  UserCheck,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { formatModePill } from "../../components/match/matchHelpers";
import { cn } from "../../lib/utils";
import { trpc } from "../../lib/trpc";
import { useCurrentPlayerId } from "../hooks/useCurrentPlayerId";

type FlowProgressProps = {
  completedNodes: number;
  totalNodes: number;
  currentNodeName: string;
  timeRemaining: number | null;
  isTransitioning?: boolean;
  context?: FlowContext;
  players?: FlowPlayer[];
};

const NODE_TYPE_LABELS: Record<string, string> = {
  ready_up: "Ready Check",
  map_vote: "Map Vote",
  map_ban: "Map Ban",
  auto_balance: "Team Balance",
  captain_draft: "Captain Draft",
  self_select: "Team Selection",
  match_start: "Match Start",
  match_cancelled: "Match Cancelled",
  game_mode_select: "Game Mode",
  character_ban: "Character Ban",
  character_pick: "Character Pick",
  side_select: "Side Select",
  host_instructions: "Host Setup",
  lobby_code_input: "Lobby Code",
  lobby_created: "Lobby Join",
  voice_channel_join: "Voice Channel",
  team_voice_split: "Team Voice",
};

export function FlowProgress({
  completedNodes,
  totalNodes,
  currentNodeName,
  timeRemaining,
  isTransitioning,
  context,
  players,
}: FlowProgressProps) {
  const label = isTransitioning
    ? "Next Step"
    : NODE_TYPE_LABELS[currentNodeName] || currentNodeName;

  const formatTime = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const isLowTime = timeRemaining !== null && timeRemaining < 10000;
  const isCritical = timeRemaining !== null && timeRemaining < 5000;
  const currentStep = Math.min(completedNodes + 1, totalNodes);

  const pills = context ? <ContextPills context={context} players={players} /> : null;

  return (
    <div
      role="progressbar"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={totalNodes}
      aria-label={`Step ${currentStep} of ${totalNodes}: ${label}`}
      className="relative overflow-hidden rounded-lg bg-surface-800/80 border border-surface-700/40"
    >
      {/* Subtle top edge glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-surface-500/20 to-transparent" />

      <div className="relative flex items-center gap-4 px-4 py-3">
        {/* Step indicator */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalNodes }, (_, i) => (
            <div
              key={i}
              className={cn(
                "rounded-full transition-all duration-300",
                i < completedNodes
                  ? "w-3 h-3 bg-brand-500"
                  : i === completedNodes
                    ? "w-6 h-3 bg-brand-500 rounded-full"
                    : "w-3 h-3 bg-surface-600",
              )}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-4 bg-surface-500/40" />

        {/* Node label + step text */}
        <div className="flex items-baseline gap-2 flex-1 min-w-0">
          <span className="text-sm font-semibold text-text-primary uppercase tracking-wide truncate">
            {label}
          </span>
          {totalNodes > 1 && (
            <span className="text-xs text-surface-500 tabular-nums shrink-0">
              {currentStep}/{totalNodes}
            </span>
          )}
        </div>

        {/* Timer */}
        {timeRemaining !== null && (
          <div
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-md tabular-nums shrink-0",
              "font-bold text-sm tracking-wide",
              "transition-all duration-300",
              isTransitioning
                ? "bg-brand-500/15 text-brand-400"
                : isCritical
                  ? "bg-error-500/15 text-error-400 animate-pulse"
                  : isLowTime
                    ? "bg-error-500/10 text-error-400"
                    : "bg-surface-700/50 text-text-secondary",
            )}
          >
            {/* Clock icon */}
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className={cn(
                "shrink-0 transition-colors duration-300",
                isTransitioning
                  ? "text-brand-400/70"
                  : isLowTime
                    ? "text-error-400/70"
                    : "text-surface-500",
              )}
            >
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {formatTime(timeRemaining)}
          </div>
        )}
      </div>

      {/* Context pills row */}
      {pills && <div className="relative px-4 pb-3 -mt-1">{pills}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Context pills (inlined from FlowContextBar)
// ---------------------------------------------------------------------------

type Pill = {
  icon: ReactNode;
  label: string;
  value: string;
  imageUrl?: string | null;
};

function toContextValueString(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value == null) return null;
  return JSON.stringify(value);
}

function ContextPills({ context, players }: { context: FlowContext; players?: FlowPlayer[] }) {
  const currentPlayerId = useCurrentPlayerId();

  const { data: maps } = trpc.game.getMaps.useQuery(
    { gameId: (context.gameId as string) || "" },
    { enabled: !!context.selectedMapId && !!context.gameId },
  );

  const selectedMap = maps?.find((m) => m.id === context.selectedMapId);

  const teams = context.teams as Record<string, number> | undefined;
  let myTeamName: string | null = null;
  if (teams && currentPlayerId && teams[currentPlayerId] !== undefined) {
    const teamNames = getTeamNames(context.matchId, context.teamCount);
    const myTeamNum = teams[currentPlayerId];
    myTeamName = teamNames[myTeamNum] ?? null;
  }

  const pills: Pill[] = [];

  if (context.gameName) {
    pills.push({
      icon: <Gamepad2 className="h-3 w-3" />,
      label: "Game",
      value: context.gameName,
      imageUrl: context.gameIconUrl as string | null | undefined,
    });
  }

  if (context.queueName) {
    pills.push({
      icon: <Layers className="h-3 w-3" />,
      label: "Queue",
      value: context.queueName,
      imageUrl: context.queueIconUrl as string | null | undefined,
    });
  }

  if (context.teamCount && context.teamSize) {
    pills.push({
      icon: <Swords className="h-3 w-3" />,
      label: "Format",
      value: formatModePill(context.teamCount, context.teamSize),
    });
  }

  if (selectedMap) {
    pills.push({
      icon: <MapIcon className="h-3 w-3" />,
      label: "Map",
      value: selectedMap.name,
      imageUrl: selectedMap.thumbnailUrl ?? selectedMap.imageUrl,
    });
  }

  if (myTeamName) {
    pills.push({
      icon: <Users className="h-3 w-3" />,
      label: "Team",
      value: myTeamName,
    });
  }

  const selectedGameMode = context.selectedGameMode as { name?: string } | undefined;
  if (selectedGameMode?.name) {
    pills.push({
      icon: <Swords className="h-3 w-3" />,
      label: "Mode",
      value: selectedGameMode.name,
    });
  }

  const selectedSides = context.selectedSides as Record<string, string> | undefined;
  if (selectedSides && teams && currentPlayerId) {
    const myTeam = teams[currentPlayerId];
    const mySide = myTeam !== undefined ? selectedSides[String(myTeam)] : undefined;
    if (mySide) {
      pills.push({
        icon: <Shield className="h-3 w-3" />,
        label: "Side",
        value: mySide,
      });
    }
  }

  const lobbyCode = toContextValueString(context.lobbyCode);
  if (lobbyCode) {
    pills.push({
      icon: <KeyRound className="h-3 w-3" />,
      label: "Lobby",
      value: lobbyCode,
    });
  }

  const hostUserId = (context.hostId ?? context.hostUserId) as string | undefined;
  const hostPlayer = hostUserId ? players?.find((p) => p.userId === hostUserId) : undefined;
  if (hostPlayer) {
    pills.push({
      icon: <UserCheck className="h-3 w-3" />,
      label: "Host",
      value: hostPlayer.username,
    });
  }

  const bannedCharacters = context.bannedCharacters as string[] | undefined;
  if (bannedCharacters && bannedCharacters.length > 0) {
    pills.push({
      icon: <Crosshair className="h-3 w-3" />,
      label: "Bans",
      value: `${bannedCharacters.length} banned`,
    });
  }

  const characterPicks = context.characterPicks as Record<string, string> | undefined;
  if (characterPicks && Object.keys(characterPicks).length > 0) {
    pills.push({
      icon: <Crosshair className="h-3 w-3" />,
      label: "Picks",
      value: `${Object.keys(characterPicks).length} picked`,
    });
  }

  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {pills.map((pill) => (
        <div
          key={pill.label}
          className="flex items-center gap-1 bg-surface-700/50 rounded px-2 py-0.5"
        >
          {pill.imageUrl ? (
            <img src={pill.imageUrl} alt="" className="h-3.5 w-3.5 rounded-sm object-cover" />
          ) : (
            <span className="text-surface-400">{pill.icon}</span>
          )}
          <span className="text-2xs uppercase tracking-wider text-surface-400 font-medium">
            {pill.label}
          </span>
          <span className="text-2xs font-display font-semibold text-surface-200">{pill.value}</span>
        </div>
      ))}
    </div>
  );
}
